import React from "react";
import { BlockMath, InlineMath, ReadableInlineMath } from "../../../../components/math/KatexWrappers";
import EquationInput from "../../../../components/inputs/EquationInput";
import { scoreStaged } from "../../../openTaskScoring";

const CASES = [
  { distance: 7.5, start: "8:44", rider1: "Kuba", rider2: "Paweł", vehicle1: "skuterem", vehicle2: "rowerem elektrycznym", arrival1: "9:06", speed2: 18 },
  { distance: 6, start: "10:15", rider1: "Ola", rider2: "Maja", vehicle1: "hulajnogą", vehicle2: "rowerem", arrival1: "10:33", speed2: 15 },
  { distance: 9, start: "7:50", rider1: "Adam", rider2: "Tomek", vehicle1: "skuterem", vehicle2: "rowerem", arrival1: "8:14", speed2: 20 },
  { distance: 4.8, start: "12:05", rider1: "Kasia", rider2: "Ania", vehicle1: "rowerem", vehicle2: "hulajnogą", arrival1: "12:25", speed2: 12 },
];

function pick(array) {
  return array[Math.floor(Math.random() * array.length)];
}

function minutesFromClock(clock) {
  const [h, m] = clock.split(":").map(Number);
  return h * 60 + m;
}

function formatNumber(value) {
  return Number.isInteger(value) ? String(value) : String(value).replace(".", ",");
}

function clockLatex(clock) {
  const [hour, minute] = clock.split(":");
  return `${Number(hour)}{:}${minute.padStart(2, "0")}`;
}

function distanceLatex(value) {
  return String.raw`${formatNumber(value)}\ \text{km}`;
}

function speedLatex(value) {
  return String.raw`${value}\ \dfrac{\text{km}}{\text{h}}`;
}

function minutesLatex(value) {
  return String.raw`${value}\ \text{min}`;
}

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x || 1;
}

function minutesToHourFractionLatex(minutes) {
  const d = gcd(minutes, 60);
  const n = minutes / d;
  const q = 60 / d;
  if (q === 1) return String.raw`${n}\ \text{h}`;
  return String.raw`\frac{${n}}{${q}}\ \text{h}`;
}

function rawStr(value) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value.raw === "string") return value.raw;
  if (typeof value.value === "string") return value.value;
  return "";
}

function lastNumberLatex(raw) {
  const values = numberTokens(raw);
  const value = values.length ? values[values.length - 1] : null;
  return Number.isFinite(value) ? formatNumber(value) : null;
}

function buildData(config) {
  const time1 = minutesFromClock(config.arrival1) - minutesFromClock(config.start);
  const time2 = (config.distance / config.speed2) * 60;
  const winner = time1 < time2 ? config.rider1 : config.rider2;
  return { ...config, time1, time2, winner, maxPoints: 2 };
}

function createOriginalTask() {
  return {
    type: "E8RouteTimeCompareOpenTask",
    data: buildData(CASES[0]),
    source: { variant: "original", exam: "E8", session: "2025_CZE", taskNumber: 18 },
    tags: ["e8", "predkosc-droga-czas", "jednostki", "zadanie-otwarte"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  return {
    type: "E8RouteTimeCompareOpenTask",
    data: buildData(pick(CASES.slice(1))),
    source: { variant: "random", basedOn: "E8_2025_CZE_18" },
    tags: ["e8", "predkosc-droga-czas", "jednostki", "zadanie-otwarte"],
  };
}

function normalize(raw) {
  return String(raw || "").toLowerCase().replace(/,/g, ".").replace(/\s+/g, "");
}

function numberTokens(raw) {
  return (normalize(raw).match(/\d+(?:\.\d+)?/g) || []).map(Number);
}

function hasNumber(raw, target, tolerance = 0.05) {
  return numberTokens(raw).some((value) => Math.abs(value - target) <= tolerance);
}

function timeCalculationsOk(raw1, raw2, data) {
  const first = hasNumber(raw1, data.time1);
  const second = hasNumber(raw2, data.time2) || hasNumber(raw2, data.time2 / 60);
  return first && second;
}

function conclusionOk(raw, data) {
  const text = normalize(raw);
  if (!text) return false;
  return text.includes(data.winner.toLowerCase()) || text.includes(data.time1 < data.time2 ? "pierwsz" : "drug");
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        {data.rider1} i {data.rider2} wyruszyli o godzinie <InlineMath math={clockLatex(data.start)} /> ze szkoły na basen tą samą trasą
        o długości <InlineMath math={distanceLatex(data.distance)} />. {data.rider1} wyruszył {data.vehicle1}
        i dojechał na basen o <InlineMath math={clockLatex(data.arrival1)} />. {data.rider2} przejechał tę trasę {data.vehicle2} z prędkością{" "}
        <ReadableInlineMath math={speedLatex(data.speed2)} scale={1.05} verticalAlign="-0.2em" />.
      </p>
      <p>Oblicz, która z osób przejechała tę trasę w krótszym czasie. Zapisz obliczenia.</p>
    </div>
  );
}

export function renderInput({ data }, userAnswer = {}, onChange, disabled) {
  const setField = (field) => (value) => onChange({ ...(userAnswer || {}), [field]: value });
  return (
    <div style={{ display: "grid", gap: 14, textAlign: "left" }}>
      <div>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Krok 1. Oblicz czas przejazdu pierwszej osoby.</div>
        <EquationInput value={userAnswer.time1 || ""} onChange={setField("time1")} disabled={disabled} placeholder={`np. ${data.time1} min`} showPreview={false} basis="420px" />
      </div>
      <div>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Krok 2. Oblicz czas przejazdu drugiej osoby.</div>
        <EquationInput value={userAnswer.time2 || ""} onChange={setField("time2")} disabled={disabled} placeholder={`np. ${formatNumber(data.time2)} min`} showPreview basis="420px" />
      </div>
      <div>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Krok 3. Wpisz, kto jechał krócej.</div>
        <EquationInput value={userAnswer.conclusion || ""} onChange={setField("conclusion")} disabled={disabled} placeholder={`np. ${data.winner}`} showPreview={false} basis="260px" />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer = {}) {
  const calculations = timeCalculationsOk(userAnswer.time1, userAnswer.time2, data);
  const conclusion = conclusionOk(userAnswer.conclusion, data);
  return scoreStaged({
    resultOk: conclusion,
    milestones: [calculations],
    maxScore: data.maxPoints,
    parts: { "czasy przejazdu": calculations, "wniosek": conclusion },
  });
}

export function renderAnswer({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>{"Przyk\u0142adowa poprawna odpowied\u017a:"}</div>
      <BlockMath math={`${clockLatex(data.arrival1)}-${clockLatex(data.start)}=${minutesLatex(data.time1)}`} />
      <BlockMath math={String.raw`t=\frac{${formatNumber(data.distance)}}{${data.speed2}}\ \text{h}=${minutesToHourFractionLatex(data.time2)}=${minutesLatex(data.time2)}`} />
      <BlockMath math={`${minutesLatex(data.time1)}<${minutesLatex(data.time2)}`} />
      <div>{"Odpowied\u017a: kr\u00f3cej jecha\u0142(a) "}{data.winner}{"."}</div>
    </div>
  );
}

export function renderUserAnswer(userAnswer = {}) {
  const time1 = lastNumberLatex(rawStr(userAnswer.time1));
  const time2 = lastNumberLatex(rawStr(userAnswer.time2));
  const conclusion = rawStr(userAnswer.conclusion);
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>Czas 1: {time1 ? <InlineMath math={`${time1}\\ \\text{min}`} /> : <i>(brak)</i>}</div>
      <div>Czas 2: {time2 ? <InlineMath math={`${time2}\\ \\text{min}`} /> : <i>(brak)</i>}</div>
      <div>Wniosek: {conclusion || <i>(brak)</i>}</div>
    </div>
  );
}

export function renderExplanation({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p><strong>Krok 1.</strong> Obliczamy czas przejazdu {data.rider1}.</p>
      <BlockMath math={`${clockLatex(data.arrival1)}-${clockLatex(data.start)}=${minutesLatex(data.time1)}`} />
      <p><strong>Krok 2.</strong> Obliczamy czas przejazdu {data.rider2} ze wzoru <InlineMath math={String.raw`t=\frac{s}{v}`} />.</p>
      <BlockMath math={String.raw`t=\frac{${formatNumber(data.distance)}}{${data.speed2}}\ \text{h}=${minutesToHourFractionLatex(data.time2)}=${minutesLatex(data.time2)}`} />
      <p><strong>Krok 3.</strong> Porównujemy czasy.</p>
      <BlockMath math={`${minutesLatex(data.time1)}<${minutesLatex(data.time2)}`} />
      <p>Krótszy czas przejazdu miał {data.winner}.</p>
    </div>
  );
}